/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useUser } from '#hooks/UNSAFE_useUser';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { classNames } from '#utils/UNSAFE_classNames';
import { CircleWrapper, CircleInner } from '#PRIVATE_Meter';
import { CircularReferenceLine } from './CircularReferenceLine';

import {
  validateRange,
  getTrackAndIndicatorColor,
  getMeterAriaProps,
  getCircleSection,
  getPositiveAngle
} from '#utils/PRIVATE_meterUtils';

import { getDimensions, getValue, getCenterContext } from './circleMeterUtils';

import {
  getIndicatorProps,
  getTrackProps,
  getThresholdsProps,
  MeterInfo,
  getMaxWidth,
  getSizedMargin,
  SIZED_HALF_METER_HORIZ_OFFSET
} from './layoutUtils';

import {
  usePointerEvents,
  useKeyboardEvents,
  useMeterDatatip
} from '#hooks/PRIVATE_useVisMeterEvents';

import { MeterProps, CircularRefLine } from './meterCircle.types';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { MeterCircleStyles, MeterCircleVariantOptions } from './themes/MeterCircleStyles.css';
import { MeterCircleRedwoodTheme } from './themes/redwood/MeterCircleTheme';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import {
  getDefaultReferenceLineLabelFontSize,
  getMaxRefLabelDims,
  hasLabelledReferenceLine
} from '#utils/PRIVATE_meterUtils/meterUtils';
import { meterCircleVars } from './themes/MeterCircleContract.css';
import { useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { getReferenceLineLabelGap, getReferenceLinesProps } from './refLineUtils';
import { useCssVars } from '#hooks/PRIVATE_useCssVars';
import { typographyVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';

type SizedMeterProps = MeterProps & { size?: 'sm' | 'md' | 'lg' };

/**
 * A Meter circle displays information graphically in a circular bar, highlighting a specific metric value's progress in relation to its min, max, or thresholds.
 */
export function SizedMeterCircle({
  max = 100,
  min = 0,
  value = 0,
  step = 1,
  size = 'lg',
  startAngle = 90,
  angleExtent = 360,
  isTrackRendered = true,
  thresholdDisplay = 'all',
  indicatorSize = 1,
  testId,
  isReadonly,
  ...props
}: SizedMeterProps) {
  const testIdProps = useTestId(testId);

  validateRange(min, max, value, step, false, isReadonly);
  const currentInputRef = useRef<number>();
  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  let sAngle = startAngle;
  if (isRtl) {
    sAngle = getPositiveAngle(180 - sAngle);
  }

  const section = getCircleSection(sAngle, angleExtent, isRtl);
  const dimensionsRef = useRef(getDimensions(null, section));
  const circleWrapperRef = useRef<HTMLDivElement>(null);
  const [isDimsReady, setDimsReady] = useState<boolean>(false);

  useEffect(() => {
    if (props.onCommit || props.onInput || props.children) {
      dimensionsRef.current = getDimensions(circleWrapperRef.current, section);
      setDimsReady(true);
    }
  }, [size, section, props.onCommit, props.onInput, props.children]);

  const { textMeasureContent, getTextDimensions } = useTextDimensions();

  const getValueFromEvent = (event: PointerEvent) => {
    const circleContainer = circleWrapperRef.current;
    if (event.target == circleContainer) {
      return getValue(event, min, max, step, dimensionsRef, sAngle, angleExtent, section, isRtl);
    }
    return;
  };

  const isPointerActiveRef = useRef(false);

  const pointerEventsProps = usePointerEvents(
    value,
    getValueFromEvent,
    circleWrapperRef,
    currentInputRef,
    isPointerActiveRef,
    props.onCommit,
    props.onInput
  );

  const keyboardEventsProps = useKeyboardEvents(
    value,
    min,
    max,
    step,
    currentInputRef,
    props.onCommit,
    props.onInput
  );

  const { datatipContent, datatipProps } = useMeterDatatip(
    value,
    isPointerActiveRef,
    props.datatip,
    props['aria-describedby']
  );

  const ariaProps = getMeterAriaProps(
    value,
    min,
    max,
    props['aria-label'],
    props['aria-labelledby'],
    props.thresholds,
    isReadonly
  );
  const mergedProps = mergeProps(pointerEventsProps, keyboardEventsProps, datatipProps, ariaProps);

  const { trackColor, indicatorColor } = getTrackAndIndicatorColor(
    value,
    thresholdDisplay,
    props.trackColor,
    props.indicatorColor,
    props.thresholds
  );

  const orientation =
    section === 'bottom' || section === 'top'
      ? 'Horizontal'
      : section === 'left' || section === 'right'
      ? 'Vertical'
      : 'Full';

  const meterInfo = {
    min,
    max,
    value,
    startAngle: sAngle,
    angleExtent,
    size,
    section,
    isRtl,
    innerRadius: dimensionsRef.current.innerRadius
  } as MeterInfo;
  const isInteractive = props.onCommit || props.onInput;

  const { classes, styles } = useComponentTheme<MeterCircleVariantOptions, MeterCircleStyles>(
    MeterCircleRedwoodTheme,
    {
      size,
      interaction: isInteractive ? 'isInteractive' : undefined
    }
  );

  const { isTabbable } = useTabbableMode();
  const centerContext = getCenterContext(dimensionsRef, section);
  const indicatorProps = getIndicatorProps(meterInfo, indicatorSize, props.innerRadius);

  const hasReferenceLabel = hasLabelledReferenceLine(props.referenceLines);

  const breakPoint = size === 'lg' ? 'sm' : 'min';

  const fontSize = getDefaultReferenceLineLabelFontSize(breakPoint);
  const circleVars = useMemo(
    () => [
      {
        tokenVar: fontSize,
        key: 'labelFontSize',
        cssProp: 'font-size'
      },
      {
        tokenVar: typographyVars.body.md.lineHeight,
        key: 'lineHeight',
        cssProp: 'line-height'
      }
    ],
    [fontSize]
  );

  const { resolvedVars, cssContent } = useCssVars(circleVars);
  const { maxRefLabelDims } = getMaxRefLabelDims(
    resolvedVars['fontSize'],
    resolvedVars['lineHeight'],
    props.referenceLines,
    getTextDimensions
  );

  const trackProps = getTrackProps(
    meterInfo,
    isTrackRendered,
    thresholdDisplay,
    trackColor,
    props.innerRadius,
    props.thresholds
  );

  return (
    <>
      <div
        class={classNames([classes])}
        tabIndex={!isTabbable ? -1 : 0}
        {...testIdProps}
        {...mergedProps}>
        <CircleWrapper
          wrapperRef={circleWrapperRef}
          class={classNames([
            styles.circleWrapperBaseStyle,
            isInteractive ? styles.interactiveStyle : '',
            styles[`meterCircle${orientation}SizeStyle`]
          ])}
          style={{
            margin: getSizedMargin(
              size,
              section,
              !!props.referenceLines?.length,
              hasReferenceLabel,
              maxRefLabelDims
            )
          }}>
          <CircleInner
            class={classNames([styles.circleTrackBaseStyle, styles[`${section}Style`]])}
            {...trackProps}
          />
          {thresholdDisplay === 'all' &&
            props.thresholds &&
            getThresholdsProps(
              meterInfo,
              props.thresholds,
              isTrackRendered,
              trackColor,
              props.innerRadius
            ).map((threshold) => {
              return (
                <CircleInner
                  class={classNames([styles.circleTrackBaseStyle, styles[`${section}Style`]])}
                  {...threshold}
                />
              );
            })}

          <CircleInner
            color={indicatorColor}
            class={classNames([
              styles.circleValueBaseStyle,
              styles[`circleValue${section}Style`],
              styles[`${section}Style`]
            ])}
            {...indicatorProps}
          />
          {props.referenceLines?.map((refLine: CircularRefLine) => {
            const { radius, length, angle, color } = getReferenceLinesProps(
              meterInfo,
              refLine,
              props.innerRadius
            );
            return (
              <CircularReferenceLine
                label={refLine.label}
                labelStyle={{
                  fontSize: resolvedVars['labelFontSize'],
                  ...refLine.labelStyle
                }}
                labelMaxWidth={getMaxWidth(
                  meterCircleVars['size'],
                  angle,
                  `calc( ${radius} + ${length} - ${trackProps.size} + ${getReferenceLineLabelGap(
                    breakPoint,
                    angle
                  )}px)`
                )}
                radius={radius}
                labelOffset={getReferenceLineLabelGap(breakPoint, angle)}
                length={length}
                angle={angle}
                color={color}
                halfMeterOffset={SIZED_HALF_METER_HORIZ_OFFSET}
                section={section}
              />
            );
          })}
          {isDimsReady && props.children && centerContext && (
            <div class={classNames([styles.meterCircleCenterContent])}>
              {props.children(centerContext)}
            </div>
          )}
        </CircleWrapper>
      </div>
      {datatipContent}
      {textMeasureContent}
      {cssContent}
    </>
  );
}
